import { memo, useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { styled } from '@mui/system';
import { format } from 'date-fns';
import { analyticsActions, surveysActions } from '@actions';
import { subtractYears } from '@helpers/dates';
import { useLocations } from '@hooks';
import { PrintButton } from '@features/Analytics/Common/PrintButton';
import { AnalyticsContainer, HeaderWrapper } from '@features/Analytics/styles';
import AnalyticsWrapper from '@features/Analytics/AnalyticsWrapper';
import FiltersPanel from './FiltersPanel';
import ConversionTable from './ConversionTable';
import ConversionRates from './ConversionRates';

const FiltersWrapperCss = styled(HeaderWrapper)({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    '@media (max-width: 475px)': {
        gap: 8,
        flexWrap: 'wrap',
    },
});

const Conversion = memo(() => {
    const [filter, setFilter] = useState({
        locations: [],
        timeframe: {
            start: subtractYears(new Date(), 1),
            end: new Date(),
        },
    });
    const [filterNumber, setFilterNumber] = useState(0);
    const [filterOpened, setFiltersOpened] = useState(false);

    const language = useSelector(state => state.auth.user?.language);
    const { surveysTableData, qrTableData, total, conversionLoading } = useSelector(state => state.analytics);

    const dispatch = useDispatch();
    const { mapLocationsToFormValue } = useLocations();
    const firstRender = useRef(true);

    useEffect(() => {
        dispatch(surveysActions.getAll());
    }, []);

    useEffect(() => {
        setFilter(prev => ({ ...prev, locations: mapLocationsToFormValue() }));
    }, [dispatch]);

    const onApplyFilters = () => {
        dispatch(
            analyticsActions.getAnalyticsConversion({
                locations: filter.locations.map(item => item.id),
                from: filter.timeframe.start ? format(new Date(filter.timeframe.start), 'yyyy-MM-dd') : null,
                to: filter.timeframe.end ? format(new Date(filter.timeframe.end), 'yyyy-MM-dd') : null,
            }),
        );
    };

    useEffect(() => {
        if (filter.locations.length) {
            if (firstRender.current) {
                onApplyFilters();
                firstRender.current = false;
            }
        }
    }, [filter, dispatch]);

    return (
        <AnalyticsWrapper>
            <FiltersWrapperCss>
                <FiltersPanel
                    setFiltersOpened={setFiltersOpened}
                    filtersNumber={filterNumber}
                    filterOpened={filterOpened}
                    filter={filter}
                    setFilter={setFilter}
                    type="conversion"
                    setFilterNumber={setFilterNumber}
                    onApplyFilters={onApplyFilters}
                />
                <PrintButton />
            </FiltersWrapperCss>
            <AnalyticsContainer id="pl-content-analytics-conversion">
                <ConversionRates language={language} data={total} isLoading={conversionLoading} />
                <ConversionTable isLoading={conversionLoading} data={surveysTableData} filter={filter} type="surveys" />
                <ConversionTable isLoading={conversionLoading} data={qrTableData} filter={filter} type="qr" />
            </AnalyticsContainer>
        </AnalyticsWrapper>
    );
});

Conversion.displayName = 'Conversion';

export default Conversion;
